import type { Category } from '../types';

export const insurance: Category = {
  slug: "insurance-for-delivery",
  half: "business",
  accent: '#E08A00',
  icon: "shield",
  label: { en: "Insurance for delivery", es: "Seguro para entregas" },
  blurb: {
    en: "Why personal auto may not cover deliveries, and the rideshare/delivery endorsement to ask for.",
    es: "Por qué tu seguro de auto personal quizá no cubra entregas, y el endoso de rideshare/entregas que debes pedir.",
  },
  guides: [
    {
      slug: "personal-auto-gap",
      related: ["insurance-for-delivery/the-endorsement-to-ask-for", "if-you-have-an-accident/incident-checklist"],
      body: {
        en: {
          title: "The personal auto gap",
          summary: "Most personal auto policies exclude commercial use — and delivering groceries for pay is commercial use.",
          sections: [
            {
              heading: "The problem in one paragraph",
              paragraphs: [
                "A standard personal auto policy is priced for commuting and errands. Many policies exclude using the car to deliver goods or carry passengers for pay. If you're in an accident on a run and your insurer decides you were working, a claim can be reduced or denied — and some insurers cancel the policy afterward.",
              ],
            },
            {
              heading: "When you're \"working\"",
              paragraphs: [
                "Insurers often look at whether you were on the way to a store, carrying a customer's order, or driving between stops. That's most of a shift. Don't assume the gap only applies to the minute a bag is in your trunk.",
              ],
            },
            {
              heading: "What Grocery List Plus asks of you",
              paragraphs: [
                "At onboarding every Runner attests to carrying the coverage required for their delivery mode. Grocery List Plus does not provide auto insurance for Runners — you are an independent business, and your policy is yours. Keep it current; a lapse can affect your ability to run.",
              ],
            },
            {
              heading: "Not driving a car?",
              paragraphs: [
                "The launch is car-first, but bike, e-bike, scooter and on-foot Runners have different needs — a renter's or homeowner's liability policy, a bike policy, or nothing extra at all, depending on where you live. Ask an agent what applies to your mode.",
              ],
            },
          ],
          actions: [
            "Read the exclusions section of your current policy this week.",
            "Call your agent and ask directly: \"Am I covered while delivering groceries for pay?\"",
            "Write down the answer, the date and who you spoke with.",
            "If the answer is no or unclear, read the next guide.",
          ],
        },
        es: {
          title: "El hueco del seguro de auto personal",
          summary: "La mayoría de las pólizas de auto personales excluyen el uso comercial, y entregar víveres por pago es uso comercial.",
          sections: [
            {
              heading: "El problema en un párrafo",
              paragraphs: [
                "Una póliza de auto personal estándar está pensada para ir al trabajo y hacer mandados. Muchas pólizas excluyen usar el auto para entregar bienes o llevar pasajeros por pago. Si tienes un accidente en una ruta y tu aseguradora decide que estabas trabajando, un reclamo puede reducirse o negarse, y algunas aseguradoras cancelan la póliza después.",
              ],
            },
            {
              heading: "Cuándo estás \"trabajando\"",
              paragraphs: [
                "Las aseguradoras suelen fijarse en si ibas camino a una tienda, llevabas el pedido de un cliente o manejabas entre paradas. Eso es casi todo un turno. No asumas que el hueco aplica solo al minuto en que una bolsa está en tu cajuela.",
              ],
            },
            {
              heading: "Lo que Lista de Compras Plus te pide",
              paragraphs: [
                "Al incorporarse, cada Runner declara que tiene la cobertura requerida para su modo de entrega. Lista de Compras Plus no proporciona seguro de auto a los Runners: eres un negocio independiente y tu póliza es tuya. Mantenla vigente; un lapso puede afectar tu posibilidad de hacer rutas.",
              ],
            },
            {
              heading: "¿No manejas auto?",
              paragraphs: [
                "El lanzamiento está enfocado en autos, pero los Runners en bicicleta, bici eléctrica, scooter o a pie tienen necesidades distintas: una póliza de responsabilidad civil de inquilino o propietario, una póliza para bicicleta, o nada adicional, según dónde vivas. Pregúntale a un agente qué aplica a tu modo.",
              ],
            },
          ],
          actions: [
            "Lee esta semana la sección de exclusiones de tu póliza actual.",
            "Llama a tu agente y pregunta directo: \"¿Estoy cubierto mientras entrego víveres por pago?\"",
            "Anota la respuesta, la fecha y con quién hablaste.",
            "Si la respuesta es no o no queda clara, lee la siguiente guía.",
          ],
        },
      },
    },
    {
      slug: "the-endorsement-to-ask-for",
      related: ["insurance-for-delivery/personal-auto-gap", "recordkeeping-and-money/receipts-and-bookkeeping"],
      body: {
        en: {
          title: "The endorsement to ask for",
          summary: "The words that close the gap: a rideshare or delivery endorsement, or a commercial policy.",
          sections: [
            {
              heading: "Two ways to close the gap",
              paragraphs: [
                "A rideshare or delivery endorsement adds delivery use to your existing personal policy. It's usually the simplest route and the one to ask about first.",
                "A commercial auto policy replaces your personal policy and is built for business use. It's more than most part-time Runners need, but right for some. An agent can tell you which fits your situation.",
              ],
            },
            {
              heading: "How to ask",
              bullets: [
                "\"I deliver groceries as an independent contractor. Do you offer a rideshare or delivery endorsement?\"",
                "\"Does it cover me the whole time I'm working — driving to the store, carrying an order, and between stops?\"",
                "\"What are the limits and the deductible while I'm delivering?\"",
                "\"Will adding it change my premium or my renewal?\"",
                "Get the answers in writing.",
              ],
            },
            {
              heading: "What to look for on the document",
              paragraphs: [
                "Once it's added, your declarations page should name the endorsement. Keep a copy in the same folder as your receipts — you'll want it at renewal and if Grocery List Plus ever asks you to re-confirm coverage.",
              ],
            },
            {
              heading: "No prices here, on purpose",
              paragraphs: [
                "Premiums vary by state, vehicle, driving record and insurer, and they change often. Rather than quote a number the Kit can't keep accurate, get two quotes and compare. The cost is a business expense — keep the receipt.",
              ],
            },
          ],
          actions: [
            "Call your agent or insurer and ask the four questions above.",
            "Get at least one competing quote.",
            "Once it's added, save your declarations page in your records folder.",
            "Set a reminder to re-check coverage before each renewal.",
          ],
        },
        es: {
          title: "El endoso que debes pedir",
          summary: "Las palabras que cierran el hueco: un endoso de rideshare o entregas, o una póliza comercial.",
          sections: [
            {
              heading: "Dos formas de cerrar el hueco",
              paragraphs: [
                "Un endoso de rideshare o entregas agrega el uso para entregas a tu póliza personal actual. Suele ser la vía más simple y la primera por la que debes preguntar.",
                "Una póliza de auto comercial reemplaza tu póliza personal y está hecha para uso de negocio. Es más de lo que necesita la mayoría de los Runners de medio tiempo, pero es la correcta para algunos. Un agente puede decirte cuál se ajusta a tu situación.",
              ],
            },
            {
              heading: "Cómo preguntar",
              bullets: [
                "\"Entrego víveres como contratista independiente. ¿Ofrecen un endoso de rideshare o entregas?\"",
                "\"¿Me cubre todo el tiempo que estoy trabajando: manejando a la tienda, llevando un pedido y entre paradas?\"",
                "\"¿Cuáles son los límites y el deducible mientras hago entregas?\"",
                "\"¿Agregarlo cambia mi prima o mi renovación?\"",
                "Pide las respuestas por escrito.",
              ],
            },
            {
              heading: "Qué buscar en el documento",
              paragraphs: [
                "Una vez agregado, tu página de declaraciones debe nombrar el endoso. Guarda una copia en la misma carpeta que tus recibos; la vas a querer en la renovación y si Lista de Compras Plus alguna vez te pide reconfirmar tu cobertura.",
              ],
            },
            {
              heading: "Sin precios aquí, a propósito",
              paragraphs: [
                "Las primas varían según el estado, el vehículo, el historial de manejo y la aseguradora, y cambian con frecuencia. En lugar de citar un número que el Kit no puede mantener exacto, pide dos cotizaciones y compara. El costo es un gasto del negocio: guarda el recibo.",
              ],
            },
          ],
          actions: [
            "Llama a tu agente o aseguradora y haz las cuatro preguntas de arriba.",
            "Consigue al menos una cotización de la competencia.",
            "Cuando lo agreguen, guarda tu página de declaraciones en tu carpeta de registros.",
            "Pon un recordatorio para revisar tu cobertura antes de cada renovación.",
          ],
        },
      },
    },
  ],
};
